import client from './client'
import type {
  ChangePreview, ChangeRequest, ExecuteChangeResponse,
} from '../types/subscription'

export interface SubscriptionSummary {
  has_subscription: boolean
  plan_slug: string | null
  plan_name: string | null
  status: string | null
  is_beta: boolean
  is_founder: boolean
  selected_industry: string | null
  /** Onboarding's profile.industry, normalized to hyphen-style. UI default
   *  for the add-addon picker pre-fill. Never used for gating. */
  profile_industry: string
  billing_cycle: string | null
  cancel_at_period_end: boolean
  is_seat_user: boolean
  seat_owner_email: string | null
  /** Phase 8 — cores the user can chat with (includes trial bypass +
   *  profile-only beta-flag bypass). Use for chat-gate decisions. */
  chat_accessible_core_agent_ids: string[]
  /** Phase 8 — cores the user strictly OWNS via paid sub or comp-beta
   *  sub. Use for ownership UI ("✓ Active" badges, "Already subscribed"). */
  owned_core_agent_ids: string[]
  industry_agent_ids: string[]
  // Phase 5e — industry addon
  addon_industry_quantity: number
  addon_industry_active: boolean
  // C1 — trial state
  is_trial_active: boolean
  trial_messages_remaining: number
  trial_messages_allowed: number
  // Phase 5f — pending downgrade (SubscriptionSchedule)
  has_pending_change: boolean
  pending_change_at: string | null
  pending_plan_slug: string
  pending_billing_cycle: string
  pending_addon_quantity: number | null
}

export async function getSubscriptionSummary(): Promise<SubscriptionSummary> {
  const { data } = await client.get<SubscriptionSummary>('/api/me/subscription-summary/')
  return data
}

export interface NeedsSelectionResponse {
  needs_selection: boolean
  reason: string | null
}

export async function checkNeedsIndustrySelection(): Promise<NeedsSelectionResponse> {
  const { data } = await client.get<NeedsSelectionResponse>('/api/me/needs-industry-selection/')
  return data
}

export async function selectBetaIndustry(industrySlug: string): Promise<{ success: boolean; selected_industry: string }> {
  const { data } = await client.post('/api/me/select-beta-industry/', {
    industry_slug: industrySlug,
  })
  return data
}

export async function previewSubscriptionChange(request: ChangeRequest): Promise<ChangePreview> {
  const { data } = await client.post<ChangePreview>('/api/subscription/preview-change/', request)
  return data
}

// ─── Phase 4f-1 — execute-change ──────────────────────────────────

const PREVIEW_TO_EXECUTE_CHANGE_TYPE: Record<ChangeRequest['proposed_change'], string> = {
  cancel: 'cancel_subscription',
  switch_plan: 'switch_plan',
  switch_industry: 'switch_industry',
  switch_billing_cycle: 'switch_billing_cycle',
  add_agent: 'add_agent',
  remove_agent: 'remove_agent',
  swap_agent: 'swap_agent',
  add_seat: 'add_seat',
  remove_seat: 'remove_seat',
  reactivate: 'reactivate',
  add_industry_addon: 'add_industry_addon',
  remove_industry_addon: 'remove_industry_addon',
  switch_industry_addon: 'switch_industry_addon',
  cancel_scheduled_change: 'cancel_scheduled_change',
}

/** Generate a fresh idempotency key for the lifecycle of one warning modal. */
export function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Last-ditch fallback for ancient browsers — still sufficient for idempotency.
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

/** Translate the preview-side ChangeRequest into the execute-side
 *  body shape. Maps proposed_change → change_type and renames per-action
 *  fields. */
function buildExecuteBody(
  request: ChangeRequest,
  confirmationToken: string,
  idempotencyKey: string,
): Record<string, unknown> {
  const change_type = PREVIEW_TO_EXECUTE_CHANGE_TYPE[request.proposed_change]
  const base: Record<string, unknown> = {
    change_type,
    confirmation_token: confirmationToken,
    request_idempotency_key: idempotencyKey,
    forfeit_legacy: request.forfeit_legacy ?? false,
  }
  switch (request.proposed_change) {
    case 'cancel':
      return base
    case 'switch_plan':
      return {
        ...base,
        target_plan_slug: request.new_plan,
        target_billing_cycle: request.billing_cycle,
        target_industry: request.new_industry,
        selected_core_agents: request.selected_core_agents,
        selected_industry_agents: request.selected_industry_agents,
      }
    case 'switch_industry':
      return { ...base, target_industry: request.new_industry }
    case 'switch_billing_cycle':
      return { ...base, target_billing_cycle: request.billing_cycle }
    case 'swap_agent':
      return {
        ...base,
        swap_from_agent_id: request.out_agent_slug,
        swap_to_agent_id: request.in_agent_slug,
      }
    case 'add_seat':
    case 'remove_seat':
      return { ...base, seat_email: request.seat_email }
    case 'add_agent':
    case 'remove_agent':
      // Backend handlers reject these with INVALID_CHANGE_TYPE; keep
      // the field forwarding so the error message is consistent.
      return { ...base, agent_id: request.agent_slug }
    case 'add_industry_addon':
      return {
        ...base,
        addon_quantity: request.addon_quantity,
        industry_slug: request.industry_slug,
        selected_industry_agents: request.selected_industry_agents,
      }
    case 'switch_industry_addon':
      return {
        ...base,
        target_quantity: request.target_quantity,
        selected_industry_agents: request.selected_industry_agents,
      }
    case 'remove_industry_addon':
      return base
    case 'cancel_scheduled_change':
      return base
    default:
      return base
  }
}

export async function executeSubscriptionChange(
  request: ChangeRequest,
  confirmationToken: string,
  idempotencyKey: string,
): Promise<ExecuteChangeResponse> {
  const body = buildExecuteBody(request, confirmationToken, idempotencyKey)
  try {
    const { data } = await client.post<ExecuteChangeResponse>(
      '/api/subscription/execute-change/', body,
    )
    return data
  } catch (e: any) {
    // 4xx with structured error_code body — surface that, don't throw.
    const data = e?.response?.data
    if (data && typeof data === 'object' && 'error_code' in data) {
      return {
        success: false,
        change_log_id: null,
        new_subscription: null,
        stripe_invoice_url: null,
        error_code: data.error_code ?? 'UNKNOWN',
        error_message: data.error_message ?? 'Unexpected error.',
      }
    }
    throw e
  }
}

export interface AccountAgentInfo {
  agent_id: string
  name: string
  image_url: string
  emoji: string
  color: string
}

export interface AccountOverview {
  subscription: {
    has_subscription: boolean
    plan_slug: string | null
    plan_name: string | null
    plan_tagline: string | null
    plan_description: string
    status: string | null
    is_beta: boolean
    is_founder: boolean
    billing_cycle: string | null
    cancel_at_period_end: boolean
    current_period_end: string | null
    monthly_price_cents: number | null
    annual_price_cents: number | null
    effective_monthly_price_cents: number | null
    locked_price_cents: number | null
    selected_industry: string | null
    selected_industry_display: string | null
  }
  agents: {
    core: AccountAgentInfo[]
    industry: AccountAgentInfo[]
    core_total_available: number
    industry_total_available: number
  }
  billing: {
    has_payment_method: boolean
    default_card_last4: string | null
    default_card_brand: string | null
    next_charge_amount_cents: number | null
    next_charge_date: string | null
  }
  seats: {
    is_agency: boolean
    total_seats: number
    used_seats: number
    pending_invites: number
  } | null
}

export async function getAccountOverview(): Promise<AccountOverview> {
  const { data } = await client.get<AccountOverview>('/api/me/account-overview/')
  return data
}

// ─── Phase 4e — payment methods + invoices ─────────────────────────

export interface PaymentMethod {
  id: string
  brand: string
  last4: string
  exp_month: number
  exp_year: number
  is_default: boolean
}

export interface PaymentMethodList {
  payment_methods: PaymentMethod[]
  has_methods: boolean
  default_pm_id: string | null
}

export interface SetupIntentResponse {
  client_secret: string
  customer_id: string
  mode: 'live' | 'test'
}

export interface Invoice {
  id: string
  number: string
  created: string | null
  amount_paid_cents: number
  amount_due_cents: number
  currency: string
  status: string
  hosted_invoice_url: string
  invoice_pdf: string
  description: string
}

export async function listPaymentMethods(): Promise<PaymentMethodList> {
  const { data } = await client.get<PaymentMethodList>('/api/me/payment-methods/')
  return data
}

export async function createSetupIntent(): Promise<SetupIntentResponse> {
  const { data } = await client.post<SetupIntentResponse>('/api/me/payment-methods/setup-intent/')
  return data
}

export async function setDefaultPaymentMethod(paymentMethodId: string): Promise<{ success: boolean; default_pm_id: string }> {
  const { data } = await client.post('/api/me/payment-methods/set-default/', {
    payment_method_id: paymentMethodId,
  })
  return data
}

export async function removePaymentMethod(paymentMethodId: string): Promise<{ success: boolean }> {
  const { data } = await client.post('/api/me/payment-methods/remove/', {
    payment_method_id: paymentMethodId,
  })
  return data
}

export async function listInvoices(): Promise<Invoice[]> {
  const { data } = await client.get<{ invoices: Invoice[] }>('/api/me/invoices/')
  return data.invoices
}

// ─── Phase 4g — agency seats ───────────────────────────────────────

export interface SeatInviteInfo {
  valid: boolean
  expired?: boolean
  already_accepted?: boolean
  revoked?: boolean
  invited_email?: string
  owner_name?: string
  owner_email?: string
  plan_name?: string
  reason?: string
}

export interface SeatRow {
  id: number
  invited_email: string
  status: 'invited' | 'active' | 'revoked'
  accepted_user_email: string | null
  invited_at: string | null
  invite_expires_at: string | null
  accepted_at: string | null
  revoked_at: string | null
  is_extra_seat: boolean
}

export interface SeatList {
  seats: SeatRow[]
  included_seat_count: number
  used_count: number
  pending_count: number
  extra_seat_count: number
}

export async function fetchSeatInviteInfo(token: string): Promise<SeatInviteInfo> {
  const { data } = await client.get<SeatInviteInfo>(`/api/seat/invite/${token}/`)
  return data
}

export async function acceptSeatInvite(
  token: string,
  payload: { password: string; name?: string },
): Promise<{
  success: boolean
  access_token?: string
  refresh_token?: string
  user?: { id: number; email: string; username: string; name: string }
  error_code?: string
  error_message?: string
}> {
  try {
    const { data } = await client.post(`/api/seat/accept/${token}/`, payload)
    return data
  } catch (e: any) {
    const data = e?.response?.data
    if (data && typeof data === 'object' && 'error_code' in data) {
      return data
    }
    throw e
  }
}

export async function listAccountSeats(): Promise<SeatList> {
  const { data } = await client.get<SeatList>('/api/account/seats/')
  return data
}

export async function resendSeatInvite(seatId: number): Promise<{ success: boolean; sent: boolean; invited_email: string }> {
  const { data } = await client.post(`/api/account/seats/${seatId}/resend/`)
  return data
}
